import { useState } from 'react';
import { GROUP_FIELD_LABEL, GROUP_KEYS, MAX_GROUP_LEVELS, groupingSummary, type GroupKey } from '../lib/grouping.js';
import { Picker } from './Picker.jsx';
import { useT } from '../i18n/index.js';

const REMOVE = '';

/** The `»` chip on the filter row (D-38): shows or hides the grouping bar; when hidden it still names the active grouping. */
export function GroupToggle({ open, onToggle, groups }: { open: boolean; onToggle: () => void; groups: GroupKey[] }) {
  const t = useT();
  const summary = !open && groups.length ? groupingSummary(groups) : null;
  return (
    <button type="button" className="chip" data-group-toggle data-on={open || groups.length > 0} onClick={onToggle} title={t('group.toggle')} aria-expanded={open}>
      <kbd>g</kbd>
      <span aria-hidden>{open ? '«' : '»'}</span>
      {summary ? <span className="max-w-56 truncate">{summary}</span> : t('group.by')}
    </button>
  );
}

/**
 * The grouping bar (D-38): "Group by [Status ▾] then [Stage ▾] then [+ ▾]". Each slot is a
 * single-choice picker over the fields not used by the other slots; picking "Remove this
 * level" drops the slot. Up to three levels.
 */
export function GroupBar({ groups, setGroups }: { groups: GroupKey[]; setGroups: (g: GroupKey[]) => void }) {
  const t = useT();
  const [slot, setSlot] = useState<{ index: number; anchor: { x: number; y: number } } | null>(null);
  const slots = groups.length < MAX_GROUP_LEVELS ? [...groups, null] : groups;

  const options = slot
    ? [
        ...GROUP_KEYS.filter((k) => !groups.includes(k) || groups[slot.index] === k).map((k) => ({ value: k, label: t(GROUP_FIELD_LABEL[k]) })),
        ...(groups[slot.index] ? [{ value: REMOVE, label: t('group.remove') }] : []),
      ]
    : [];

  const pick = (value: string) => {
    if (!slot) return;
    const next = [...groups];
    if (value === REMOVE) next.splice(slot.index, 1);
    else next[slot.index] = value as GroupKey;
    setGroups(next.slice(0, MAX_GROUP_LEVELS));
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-line bg-surface px-4 py-1.5 text-xs text-muted" data-group-bar>
      <span>{t('group.by')}</span>
      {slots.map((k, i) => (
        <span key={i} className="contents">
          {i > 0 && <span>{t('group.then')}</span>}
          <button
            type="button"
            className="chip"
            data-group-slot={i}
            data-on={!!k}
            onClick={(e) => {
              const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
              setSlot((s) => (s?.index === i ? null : { index: i, anchor: { x: r.left, y: r.bottom + 6 } }));
            }}
          >
            {k ? t(GROUP_FIELD_LABEL[k]) : t('group.add')}
            <span className="text-muted">▾</span>
          </button>
        </span>
      ))}
      {groups.length > 0 && (
        <button type="button" className="chip" onClick={() => setGroups([])}>
          {t('group.clear')}
        </button>
      )}
      {slot && (
        <Picker
          title={t('group.level', { n: slot.index + 1 })}
          options={options}
          selected={groups[slot.index] ? [groups[slot.index]!] : []}
          onToggle={pick}
          onClose={() => setSlot(null)}
          anchor={slot.anchor}
        />
      )}
    </div>
  );
}
